/**
 * Deep-link map for the assistant action router (design doc §7).
 *
 * After a kind:'action' result the dock/full-screen assistant navigates to the
 * created/updated entity. `routeForTarget` maps an AssistantTarget {type,id} to a
 * concrete Expo Router path (an `Href`).
 *
 * Each mapping below was verified against the actual routes under app/(app)/:
 *   estimate       → app/(app)/estimates/[id].tsx        ✓
 *   task           → app/(app)/tasks/[id].tsx            ✓
 *   schedule_event → app/(app)/schedule/[id].tsx         ✓
 *   job            → app/(app)/jobs/[id].tsx             ✓
 *   customer       → app/(app)/customers.tsx (list)      ✓ (no per-customer detail route exists)
 *   batch          → app/(app)/review.tsx (queue)        ✓
 *   expense        → app/(app)/(tabs)/queue.tsx (list)   ✓ DEVIATION: no /expenses route exists.
 *
 * DEVIATION (documented): the design doc §7 lists expense→`/(app)/expenses`, but no
 * expenses list OR detail route exists in app/(app)/. Expenses surface inside the
 * intake queue (the projection routes receipts to expenses there), so an expense
 * target routes to the closest existing list screen, the Queue tab. When/if a
 * dedicated expenses route lands, update the single mapping below.
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import type { Href } from 'expo-router';
import type { AssistantTarget, EntityType } from '../api/assistant';

/**
 * Resolve an assistant target to an Expo Router destination.
 * Returns a typed Href ready for `router.push(...)`.
 */
export function routeForTarget(target: AssistantTarget): Href {
  const { type, id } = target;
  switch (type) {
    case 'estimate':
      return { pathname: '/(app)/estimates/[id]', params: { id } };
    case 'task':
      return { pathname: '/(app)/tasks/[id]', params: { id } };
    case 'schedule_event':
      return { pathname: '/(app)/schedule/[id]', params: { id } };
    case 'job':
      return { pathname: '/(app)/jobs/[id]', params: { id } };
    case 'customer':
      // No per-customer detail route exists yet → land on the customers list.
      return '/(app)/customers';
    case 'batch':
      // Intake batches are reviewed on the review/queue screen.
      return '/(app)/review';
    case 'expense':
      // DEVIATION (see module doc): no /expenses route → closest list is the Queue tab.
      return '/(app)/(tabs)/queue';
    default: {
      // Exhaustiveness guard — a new EntityType must add a case above.
      const _never: never = type;
      return _never;
    }
  }
}

/** Whether a target is navigable (always true today; kept for call-site clarity). */
export function isNavigableTarget(type: EntityType): boolean {
  return (
    type === 'estimate' ||
    type === 'task' ||
    type === 'schedule_event' ||
    type === 'job' ||
    type === 'customer' ||
    type === 'batch' ||
    type === 'expense'
  );
}
